import { useMemo } from "react";
import { CheckCircle2, ClipboardList, DollarSign, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatShortDate } from "@/lib/format";
import type { WorkerSummaryRow } from "./operations-tab";

type CleaningJobRow = {
  id: number;
  apartmentName?: string;
  apartmentId?: number;
  assignedToUserId?: number;
  status: string;
  priority: string;
  scheduledDate?: string | null;
  cost?: string | number | null;
};

export type WorkerDetailSheetProps = {
  worker: WorkerSummaryRow | null;
  cleaningJobs: CleaningJobRow[];
  apartmentMap: Record<number, string>;
  onClose: () => void;
};

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "completed") return "default";
  if (status === "in_progress") return "secondary";
  if (status === "cancelled") return "destructive";
  return "outline";
}

export function WorkerDetailSheet({ worker, cleaningJobs, apartmentMap, onClose }: WorkerDetailSheetProps) {
  const workerJobs = useMemo(() => {
    if (!worker) return [];
    return cleaningJobs.filter(j => j.assignedToUserId === worker.userId);
  }, [worker, cleaningJobs]);

  const completionRate = worker && worker.jobs > 0
    ? Math.round((worker.completedJobs / worker.jobs) * 100)
    : 0;

  return (
    <Sheet open={!!worker} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            {worker?.name ?? "Worker"}
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            Worker payment + job detail for the current report period.
          </p>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6 mt-2">
          {worker && (
            <div className="space-y-4 pb-6">
              <MetricStrip cols={4}>
                <KpiCard label="Jobs" value={worker.jobs} icon={ClipboardList} variant="info" />
                <KpiCard label="Completed" value={worker.completedJobs} icon={CheckCircle2} variant="success" helper={`${completionRate}% rate`} />
                <KpiCard label="Pending" value={worker.pendingJobs} icon={ClipboardList} variant="warning" />
                <KpiCard label="Earned" value={formatCurrency(Number(worker.totalPayout))} icon={DollarSign} variant="success" />
              </MetricStrip>

              <MetricStrip cols={2}>
                <KpiCard
                  label="Awaiting payment"
                  value={formatCurrency(Number(worker.pendingPayout))}
                  variant="warning"
                  helper="Jobs done, payout still owed"
                />
                <KpiCard
                  label="Estimated pending"
                  value={formatCurrency(Number(worker.estimatedPayout ?? 0))}
                  variant="info"
                  helper="Future payouts based on assigned jobs"
                />
              </MetricStrip>

              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Cleaning jobs in period ({workerJobs.length})
                </p>
                {workerJobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No jobs assigned in this period.</p>
                ) : (
                  <div className="space-y-1.5">
                    {workerJobs.map(j => (
                      <div
                        key={j.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-md border hover:bg-accent/40 transition-colors"
                        data-testid={`worker-detail-job-${j.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {j.apartmentName || apartmentMap[Number(j.apartmentId)] || `Apt #${j.apartmentId}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {j.scheduledDate ? formatShortDate(j.scheduledDate) : "no date"}
                            {j.priority === "urgent" ? " · urgent" : ""}
                          </p>
                        </div>
                        <Badge variant={statusBadgeVariant(j.status)} className="text-xs shrink-0">{j.status}</Badge>
                        <span className="text-xs font-medium w-16 text-right tabular-nums shrink-0">
                          {formatCurrency(Number(j.cost ?? 0))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default WorkerDetailSheet;
